import { createRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useLogin } from '@/hooks/useAuth';
import { resolveAuthRedirectTarget } from '@/lib/auth-redirect';
import { useAuthStore } from '@/stores/auth-store';
import { isApprovedBoundary } from '@/constants/role-policy';
import { rootRoute } from './__root';

type LoginSearch = {
  redirect?: string;
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    if (typeof search.redirect === 'string' && search.redirect.trim()) {
      return { redirect: search.redirect.trim() };
    }

    return {};
  },
  beforeLoad: ({ search }) => {
    const { isLoggedIn, user } = useAuthStore.getState();
    if (isLoggedIn && isApprovedBoundary(user?.roles)) {
      throw redirect({ href: resolveAuthRedirectTarget(search.redirect) });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const search = Route.useSearch();
  const loginMutation = useLogin(search.redirect);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10" data-ai-id="login-page">
      <div className="w-full max-w-md border border-[#ccc] bg-white p-8" data-ai-id="login-page-card">
        <div className="mb-8 text-center" data-ai-id="login-page-header">
          <p className="text-[11px] font-bold uppercase text-[#666]" data-ai-id="login-page-brand">
            CopyPaste
          </p>
          <h1 className="mt-2 text-xl font-bold text-[#333]" data-ai-id="login-page-title">
            Sign in
          </h1>
          <p className="mt-2 text-[13px] text-[#666]" data-ai-id="login-page-description">
            Browse freely as a guest. Sign in with an approved account to save favorites.
          </p>
        </div>

        <form
          className="space-y-4"
          data-ai-id="login-form"
          onSubmit={(event) => {
            event.preventDefault();
            loginMutation.mutate({ username, password });
          }}
        >
          {loginMutation.error ? (
            <div className="border border-[#ebccd1] bg-[#f2dede] px-4 py-3 text-[13px] text-[#a94442]" data-ai-id="login-form-error">
              {loginMutation.error.message}
            </div>
          ) : null}

          <Input
            label="Username or email"
            value={username}
            autoComplete="username"
            onChange={(event) => setUsername(event.target.value)}
            aiIdPrefix="login-form-username-field"
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aiIdPrefix="login-form-password-field"
          />

          <Button
            className="w-full"
            loading={loginMutation.isPending}
            type="submit"
            data-ai-id="login-form-submit-button"
            aiIdPrefix="login-form-submit-button"
          >
            Sign in
          </Button>
        </form>

        <p className="mt-6 text-center text-[13px] text-[#666]" data-ai-id="login-page-register-prompt">
          Need access?{' '}
          <Link to="/register" className="font-bold text-[#0055cc] hover:underline" data-ai-id="login-page-register-link">
            Request access
          </Link>
        </p>
      </div>
    </div>
  );
}
